import { useState, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { eur, num, dateParams, currentMonthRange, previousRange } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import DateRangeFilter from "@/components/DateRangeFilter";
import { ReturnsModeSelect, useReturnsMode } from "@/components/ReturnsModeSelect";
import { Delta } from "@/components/Delta";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, ChevronDown, ChevronRight } from "lucide-react";
import ExportMenu from "@/components/ExportMenu";
import { StoreArea } from "@/components/StoreArea";
import { ExpandToggle } from "@/components/ExpandToggle";
import { FirstOrderInsights } from "@/components/FirstOrderInsights";
import { SaleCustomers } from "@/components/SaleCustomers";
import { ProductionYield } from "@/components/ProductionYield";
import { MarketingInsights } from "@/components/marketing/MarketingInsights";
import { SeasonCategoryCompare } from "@/components/SeasonCategoryCompare";
import { useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { useSalesSeason } from "@/lib/salesSeason";
import { useAuth } from "@/context/AuthContext";

const PERIOD = "periodo";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))", "hsl(var(--primary))"];

const SizeGroupTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const g = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-sm shadow-md" data-testid="size-group-tooltip">
      <p className="font-semibold">{g.name} · {num(g.value)} pz</p>
      {g.sizes.map((s) => (
        <div key={s.size} className="flex justify-between gap-6 text-xs text-muted-foreground">
          <span>{s.size}</span><span>{num(s.value)} pz</span>
        </div>
      ))}
    </div>
  );
};


function Metric({ label, value, cur, prev, sub }) {
  return (
    <Card className="p-5">
      <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <div className="mt-2 flex items-baseline gap-2">
        <p className="font-display text-2xl font-bold tracking-tight">{value}</p>
        <Delta current={cur} previous={prev} />
      </div>
      {sub && <p className="mt-1 text-xs text-muted-foreground" data-testid={`metric-compare-${label}`}>{sub}</p>}
    </Card>
  );
}

export default function Vendite() {
  const [range, setRange] = useState(currentMonthRange);
  const [season, setSeason] = useState(PERIOD);
  const [compare, setCompare] = useState("");
  const bySeason = season !== PERIOD;
  const comparing = bySeason && compare && compare !== season;
  const [mode, setMode] = useReturnsMode();
  const navigate = useNavigate();
  const [showAllColors, setShowAllColors] = useState(false);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const { data: custStats } = useQuery({ queryKey: ["customers", "stats"], queryFn: async () => (await api.get("/customers/stats")).data });
  const [expanded, setExpanded] = useState({});
  const toggle = (i) => setExpanded((s) => ({ ...s, [i]: !s[i] }));
  const { data, isLoading } = useQuery({
    queryKey: ["analytics", bySeason ? season : range, mode],
    queryFn: async () => (await api.get("/sales/analytics", { params: bySeason ? { season, returns_mode: mode } : { ...dateParams(range), returns_mode: mode } })).data,
  });
  const { season: SALES_CURRENT_SEASON = "", setSeason: setRefSeason, saving: savingRef } = useSalesSeason();
  const { user: authUser } = useAuth();
  const seasons = [SALES_CURRENT_SEASON, ...(data?.seasons || []).filter((s) => s !== SALES_CURRENT_SEASON)].filter(Boolean);
  const prev = previousRange(range);
  const { data: prevData } = useQuery({
    queryKey: ["analytics", "prev", range, mode],
    queryFn: async () => (await api.get("/sales/analytics", { params: { ...dateParams(prev), returns_mode: mode } })).data,
    enabled: !!prev && !bySeason,
  });
  const { data: cmpData } = useQuery({
    queryKey: ["analytics", "season", compare, mode],
    queryFn: async () => (await api.get("/sales/analytics", { params: { season: compare, returns_mode: mode } })).data,
    enabled: !!comparing,
  });
  const cmp = bySeason ? (comparing ? cmpData : undefined) : prevData;
  const vs = (fmtv, v) => (comparing && cmpData ? `${compare}: ${fmtv(v)}` : undefined);

  return (
    <div>
      <PageHeader title="Vendite" subtitle="Analytics complete: per prodotto, categoria, colore, taglia e resa di produzione.">
        <ReturnsModeSelect value={mode} onChange={setMode} testid="vendite-returns-mode" />
        {authUser?.role === "admin" && SALES_CURRENT_SEASON && (
          <Select value={SALES_CURRENT_SEASON} onValueChange={setRefSeason} disabled={savingRef}>
            <SelectTrigger className="w-full sm:w-[230px]" title="Vale per Vendite e Panoramica (pezzi a magazzino)" data-testid="sales-ref-season"><SelectValue /></SelectTrigger>
            <SelectContent>
              {seasons.map((s) => <SelectItem key={s} value={s} data-testid={`sales-ref-season-${s}`}>Stagione di riferimento: {s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={season} onValueChange={setSeason}>
          <SelectTrigger className="w-full sm:w-[250px]" data-testid="vendite-season-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={PERIOD}>Filtra per periodo</SelectItem>
            {seasons.map((s) => <SelectItem key={s} value={s} data-testid={`vendite-season-${s}`}>Tutta la stagione {s}{s === SALES_CURRENT_SEASON ? " (corrente)" : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        {bySeason && (
          <Select value={compare || "none"} onValueChange={(v) => setCompare(v === "none" ? "" : v)}>
            <SelectTrigger className="w-full sm:w-[210px]" data-testid="vendite-compare-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nessun confronto</SelectItem>
              {seasons.filter((s) => s !== season).map((s) => <SelectItem key={s} value={s} data-testid={`vendite-compare-${s}`}>Confronta con {s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {!bySeason && <DateRangeFilter range={range} onChange={setRange} testid="vendite-date-filter" />}
        <ExportMenu base="vendite" testid="export-vendite" />
      </PageHeader>

      {isLoading || !data ? (
        <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
      <>
      {bySeason && <p className="mb-4 text-sm text-muted-foreground" data-testid="vendite-season-note">Tutte le vendite dei prodotti della stagione <b className="text-foreground">{season}</b>, da quando sono in vendita. Spese di spedizione e contrassegno escluse.</p>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label={`Fatturato totale${data.revenue_source === "shopify" ? " · Shopify" : ""}`} value={eur(data.total_revenue)} cur={data.total_revenue} prev={cmp?.total_revenue} sub={vs(eur, cmpData?.total_revenue)} />
        <Metric label="Pezzi venduti" value={num(data.total_units)} cur={data.total_units} prev={cmp?.total_units} sub={vs(num, cmpData?.total_units)} />
        <Metric label="Ordini validi" value={num(data.total_orders)} cur={data.total_orders} prev={cmp?.total_orders} sub={vs(num, cmpData?.total_orders)} />
        <Metric label="Scontrino medio" value={eur(data.avg_order_value)} cur={data.avg_order_value} prev={cmp?.avg_order_value} sub={vs(eur, cmpData?.avg_order_value)} />
      </div>

      {comparing && cmpData && <SeasonCategoryCompare a={data} b={cmpData} nameA={season} nameB={compare} />}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h3 className="mb-4 font-display text-lg font-semibold">Vendite per categoria</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data.by_category} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}>
                {data.by_category.map((c, i) => <Cell key={c.name} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 13 }} formatter={(v) => eur(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 flex flex-wrap gap-3">
            {data.by_category.map((c, i) => (
              <div key={c.name} className="flex items-center gap-1.5 text-xs">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                {c.name}
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="mb-4 font-display text-lg font-semibold">Vendite per taglia</h3>
          <ResponsiveContainer width="100%" height={260} data-testid="size-group-chart">
            <BarChart data={data.by_size_group}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip content={<SizeGroupTooltip />} cursor={{ fill: "hsl(var(--accent))" }} />
              <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
        <Card className="p-6 lg:col-span-2">
          <h3 className="mb-4 font-display text-lg font-semibold">Vendite per colore</h3>
          <div className="space-y-3">
            {(showAllColors ? data.by_color : data.by_color.slice(0, 15)).map((c) => {
              const max = data.by_color[0]?.value || 1;
              return (
                <div key={c.name}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>{c.name}</span>
                    <span className="font-semibold">{eur(c.value)}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${(c.value / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <ExpandToggle total={data.by_color.length} open={showAllColors} onToggle={() => setShowAllColors(!showAllColors)} testid="colors-toggle" />
        </Card>

        <ProductionYield season={bySeason ? season : undefined} />
      </div>

      <Card className="mt-6 overflow-hidden">
        <h3 className="px-6 py-4 font-display text-lg font-semibold border-b border-border/60">Dettaglio venduto per prodotto</h3>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-8"></TableHead>
              <TableHead>Prodotto</TableHead>
              <TableHead className="text-right">Pezzi venduti</TableHead>
              <TableHead className="text-right">Fatturato</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(showAllProducts ? data.by_product_grouped : data.by_product_grouped.slice(0, 15)).map((p, i) => {
              const multi = (p.colors?.length || 0) > 1;
              const open = !!expanded[p.name];
              return (
                <Fragment key={p.name}>
                  <TableRow className={multi ? "cursor-pointer" : ""} onClick={() => multi && toggle(p.name)} data-testid={`vendite-product-${i}`}>
                    <TableCell>{multi ? (open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />) : null}</TableCell>
                    <TableCell className="text-sm font-medium">
                      {p.name}
                      <span className="ml-2 text-xs text-muted-foreground">{multi ? `${p.colors.length} colori` : (p.colors?.[0]?.color || "")}</span>
                    </TableCell>
                    <TableCell className="text-right text-sm">{num(p.units)}</TableCell>
                    <TableCell className="text-right font-semibold">{eur(p.revenue)}</TableCell>
                  </TableRow>
                  {open && multi && p.colors.map((c, j) => (
                    <TableRow key={c.color || j} className="bg-secondary/30 hover:bg-secondary/30" data-testid={`vendite-product-${i}-color-${j}`}>
                      <TableCell></TableCell>
                      <TableCell className="pl-8 text-xs text-muted-foreground">{c.color}</TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">{num(c.units)}</TableCell>
                      <TableCell className="text-right text-xs">{eur(c.revenue)}</TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
        <div className="px-6 pb-4">
          <ExpandToggle total={data.by_product_grouped.length} open={showAllProducts} onToggle={() => setShowAllProducts(!showAllProducts)} testid="products-detail-toggle" />
        </div>
      </Card>
      <FirstOrderInsights />
      <SaleCustomers />
      {custStats && <div className="mt-6"><StoreArea stats={custStats} onChannel={(k) => navigate(`/clienti?channel=${k}`)} /></div>}
      <MarketingInsights />
      </>
      )}
    </div>
  );
}
